import { useEffect, useRef, useState, useCallback } from 'react'

import { decodeSvgBuffer } from '../../../shared/utils/svgUtils.js'
import { getCompartmentVariantUrl, getDefsUrl } from '../../../shared/utils/unitAssets.js'
import { Logger } from '../../../shared/utils/logger.js'
import { FETCH_STATUS } from '../../../shared/constants/fetchStatus.js'

import { rewriteUseHrefs } from '../utils/svgPipeline.js'

// ── Module-level cache ────────────────────────────────────────────────────────
//
// Lives outside the hook so it survives component unmounts. Variant fragments
// are tier-independent — the same compartment-variant SVG applies on every
// floor where the compartment appears — so the cache key intentionally does
// NOT include refTier.
//
// Key format: `${projectDataUrl}|${uuid}|${compartmentNumber}|${version}`
// Entry shape: SVGGElement  (the extracted <g number="N"> with default variant content
//                            replaced by the version's equipments/caseworks)
//
// Consumers must clone before injecting into the live DOM.

const MAX_CACHE_SIZE = 200

/** @type {Map<string, SVGGElement>} */
const variantCache = new Map()

// ── Cache helpers ─────────────────────────────────────────────────────────────

export function buildCacheKey(projectDataUrl, uuid, compartmentNumber, version) {
  return `${projectDataUrl}|${uuid}|${compartmentNumber}|${version}`
}

function pruneCache() {
  while (variantCache.size >= MAX_CACHE_SIZE) {
    const firstKey = variantCache.keys().next().value
    variantCache.delete(firstKey)
  }
}

/**
 * Test-only: drop every entry from the module-level cache.
 * Production code never calls this — `beforeEach` in tests does.
 */
export function __clearVariantCacheForTests() {
  variantCache.clear()
}

/**
 * Test-only: read-only access to the module cache for assertions.
 */
export function __getVariantCacheForTests() {
  return variantCache
}

// ── Parse helpers ─────────────────────────────────────────────────────────────

/**
 * Extracts the <g number="N"> compartment node from a parsed variant SVG.
 *
 * The generator emits each variant as a small standalone SVG containing one
 * compartment group. When the wrapping `<g number="N">` is omitted and the
 * equipments/caseworks sit directly under `<svg>`, we synthesize the wrapper
 * so the consumer always gets a uniform node shape.
 *
 * Exported for unit testing; production code accesses it through the hook.
 *
 * @param {SVGSVGElement} svgEl — parsed variant SVG root
 * @param {number}        compartmentNumber
 * @returns {SVGGElement}
 */
export function extractVariantGroup(svgEl, compartmentNumber) {
  const existing = svgEl.querySelector(`g[number="${compartmentNumber}"]`)
  if (existing) return existing

  // Fallback: wrap everything under <svg> in a <g number="N">.
  const svgNS = 'http://www.w3.org/2000/svg'
  const wrapper = document.createElementNS(svgNS, 'g')
  wrapper.setAttribute('number', String(compartmentNumber))
  while (svgEl.firstChild) {
    wrapper.appendChild(svgEl.firstChild)
  }
  return wrapper
}

// ── Fetch orchestrator (pure async — testable without React) ──────────────────

/**
 * Computes the list of (compartmentNumber, version) pairs that need to be
 * fetched for a product. Skips version 0 (default lives in base SVG) and any
 * key already present in the module cache.
 *
 * @returns {Array<{ compartmentNumber: number, version: number, key: string }>}
 */
export function buildPendingVariants({ projectDataUrl, uuid, variantsCount }) {
  const pending = []
  for (let i = 0; i < variantsCount.length; i++) {
    const compartmentNumber = i + 1
    const total = variantsCount[i]
    for (let v = 1; v < total; v++) {
      const key = buildCacheKey(projectDataUrl, uuid, compartmentNumber, v)
      if (!variantCache.has(key)) {
        pending.push({ compartmentNumber, version: v, key })
      }
    }
  }
  return pending
}

/**
 * Fetches, parses, and caches a single variant fragment.
 *
 * Resolves with `true` on success, `false` on a non-fatal error (logged and
 * left out of the cache). Aborts propagate to the caller as an `AbortError`
 * rejection so the outer orchestrator can short-circuit.
 *
 * Exported for unit testing — production callers go through
 * `fetchAndCacheVariants` (which iterates this).
 *
 * @param {{
 *   projectDataUrl: string,
 *   product: { uuid: string, id_cad: string },
 *   compartmentNumber: number,
 *   version: number,
 *   key: string,
 *   defsUrl: string,
 *   signal: AbortSignal,
 *   fetchImpl?: typeof fetch,
 * }} params
 * @returns {Promise<boolean>}
 */
export async function fetchAndCacheVariant({
  projectDataUrl, product, compartmentNumber, version, key, defsUrl, signal, fetchImpl,
}) {
  const doFetch = fetchImpl ?? fetch
  const url = getCompartmentVariantUrl(projectDataUrl, product, compartmentNumber, version)
  try {
    const response = await doFetch(url, { signal })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const buffer  = await response.arrayBuffer()
    const svgText = decodeSvgBuffer(buffer)

    const parser = new DOMParser()
    const doc    = parser.parseFromString(svgText, 'image/svg+xml')

    const parseError = doc.querySelector('parsererror')
    if (parseError) {
      throw new Error(`SVG parse error: ${parseError.textContent.slice(0, 200)}`)
    }

    const svgEl = doc.querySelector('svg')
    if (!svgEl) {
      throw new Error('No <svg> root element found in variant response')
    }

    const group = extractVariantGroup(svgEl, compartmentNumber)
    rewriteUseHrefs(group, defsUrl)

    pruneCache()
    variantCache.set(key, group)
    return true
  } catch (err) {
    if (err.name === 'AbortError') throw err
    Logger.warning({
      message: 'useCompartmentVariants: variant fetch failed',
      content: { url, compartmentNumber, version, error: err.message },
    })
    return false
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * useCompartmentVariants — fetches, parses, and caches per-compartment variant SVGs.
 *
 * Strategy: **eager**. As soon as `enabled` flips to true (or product changes
 * while enabled) the hook fires one `Promise.all` for every `(compartmentNumber, version)`
 * pair with `version ≥ 1`. Version 0 is never fetched — the default arrangement
 * lives in the base floor-plan SVG (read by `extractDefaultVariantNode` in
 * `utils/svgArrange.js`).
 *
 * Stale-product guard: an `AbortController` is created per effect and aborted
 * when the product changes or the component unmounts.
 *
 * Partial-failure tolerance: a missing or malformed variant logs a warning and
 * yields `null` from `getVariantNode`; the overall status still resolves to READY.
 *
 * Level 3 hook (async + external resource + module-level cache).
 *
 * @param {{
 *   product: { uuid: string }|null,
 *   projectDataUrl: string|null,
 *   variantsCount: number[],   // length === compartments.length, value = max(versions, 1)
 *   enabled: boolean,
 * }} params
 *
 * @returns {{
 *   getVariantNode: (compartmentNumber: number, version: number) => SVGGElement|null,
 *   status: string,            // FETCH_STATUS
 *   error: string|null,
 * }}
 */
export function useCompartmentVariants({ product, projectDataUrl, variantsCount, enabled }) {
  const [status, setStatus] = useState(FETCH_STATUS.READY)
  const [error, setError]   = useState(null)

  // Bumped whenever a fetch batch finishes so `getVariantNode` consumers can
  // re-read the (mutated) module cache via React's normal render cycle.
  const [, setTick] = useState(0)

  const abortRef = useRef(null)

  useEffect(() => {
    // Disabled or insufficient input — no fetch, no state churn.
    if (!enabled || !product?.uuid || !projectDataUrl || !Array.isArray(variantsCount) || variantsCount.length === 0) {
      setStatus(FETCH_STATUS.READY)
      setError(null)
      return
    }

    // Build the list of (number, version) pairs that need fetching (version ≥ 1)
    // and skip entries already in the cache.
    const pending = buildPendingVariants({
      projectDataUrl,
      uuid: product.uuid,
      variantsCount,
    })

    if (pending.length === 0) {
      setStatus(FETCH_STATUS.READY)
      setError(null)
      return
    }

    const controller = new AbortController()
    abortRef.current = controller
    setStatus(FETCH_STATUS.LOADING)
    setError(null)

    const defsUrl = getDefsUrl(projectDataUrl)

    ;(async () => {
      const results = await Promise.allSettled(
        pending.map((entry) =>
          fetchAndCacheVariant({
            projectDataUrl,
            product,
            compartmentNumber: entry.compartmentNumber,
            version: entry.version,
            key: entry.key,
            defsUrl,
            signal: controller.signal,
          }),
        ),
      )

      // If any task aborted (controller signalled) we silently bail out — a
      // newer effect run owns the state machine now.
      if (controller.signal.aborted) return

      const fatal = results.find(
        (r) => r.status === 'rejected' && r.reason?.name !== 'AbortError',
      )
      if (fatal) {
        // Should not happen — fetchAndCacheVariant swallows non-abort errors.
        setStatus(FETCH_STATUS.ERROR)
        setError(fatal.reason?.message ?? 'Unknown error loading compartment variants')
        return
      }

      setStatus(FETCH_STATUS.READY)
      setError(null)
      setTick((n) => n + 1)
    })()

    return () => {
      controller.abort()
    }
  }, [enabled, product?.uuid, projectDataUrl, variantsCount])

  const getVariantNode = useCallback(
    (compartmentNumber, version) => {
      if (!product?.uuid || !projectDataUrl || version < 1) return null
      const key = buildCacheKey(projectDataUrl, product.uuid, compartmentNumber, version)
      return variantCache.get(key) ?? null
    },
    [product?.uuid, projectDataUrl],
  )

  return { getVariantNode, status, error }
}
